import { useCallback, useEffect, useState } from 'react'
import type { AppError, HealthItem, HealthItemType, SyncSaveState } from '@/types'
import { repositories } from '@/services/repositories'
import { isAppError } from '@/lib/supabase/errors'
import {
  Banner,
  Button,
  Card,
  Checkbox,
  ConfirmDestructiveAction,
  EmptyState,
  IconButton,
  Select,
  Skeleton,
  SyncStatus,
  TextField,
  useToast,
} from '@/components/ui'
import { PillIcon, PlusIcon, TrashIcon } from '@/components/icons'
import { errorMessage, nowLocalTime } from '../saveState'

const TYPE_OPTIONS = [
  { value: 'supplement', label: 'Supplement' },
  { value: 'medication', label: 'Medicatie' },
  { value: 'other', label: 'Overig' },
]

const TYPE_LABELS: Record<HealthItemType, string> = {
  supplement: 'Supplement',
  medication: 'Medicatie',
  other: 'Overig',
}

type AddInput = { name: string; type: HealthItemType; dosage: string; schedule: string }

type HealthLibraryProps = {
  /** yyyy-mm-dd of the day the taken toggles apply to. */
  today: string
  onAnnounce: (message: string) => void
}

/**
 * Supplement & medication library: list, add, remove, and per-day taken
 * toggles. The Magnesium starting suggestion is an explicit one-tap card
 * shown only for an empty library — nothing is ever seeded silently.
 */
export function HealthLibrary({ today, onAnnounce }: HealthLibraryProps) {
  const { showToast } = useToast()
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<AppError | null>(null)
  const [items, setItems] = useState<HealthItem[]>([])
  const [takenToday, setTakenToday] = useState<Record<string, boolean>>({})
  const [saveState, setSaveState] = useState<SyncSaveState | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pendingItemId, setPendingItemId] = useState<string | null>(null)
  const [removeTarget, setRemoveTarget] = useState<HealthItem | null>(null)
  const [removing, setRemoving] = useState(false)

  const [showAddForm, setShowAddForm] = useState(false)
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [type, setType] = useState<HealthItemType>('supplement')
  const [dosage, setDosage] = useState('')
  const [schedule, setSchedule] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const [loadedItems, logs] = await Promise.all([
        repositories.health.listItems(),
        repositories.health.listLogsForDate(today),
      ])
      setItems(loadedItems)
      const taken: Record<string, boolean> = {}
      for (const log of logs) {
        taken[log.itemId] = log.taken
      }
      setTakenToday(taken)
    } catch (error) {
      setLoadError(
        isAppError(error)
          ? error
          : {
              code: 'UNKNOWN',
              userMessage: errorMessage(error),
              retryable: true,
              source: 'database',
            },
      )
    } finally {
      setLoading(false)
    }
  }, [today])

  useEffect(() => {
    void load()
  }, [load])

  const addItem = async (input: AddInput) => {
    setAdding(true)
    setActionError(null)
    try {
      const created = await repositories.health.createItem(input)
      setItems((current) => [...current, created])
      setSaveState('cloud_saved')
      showToast({ message: `${input.name} toegevoegd`, tone: 'success' })
      onAnnounce(`${input.name} is toegevoegd aan je bibliotheek.`)
      return true
    } catch (error) {
      setSaveState('sync_failed')
      const message = errorMessage(error)
      setActionError(message)
      showToast({ message, tone: 'error' })
      onAnnounce(`Toevoegen mislukt. ${message}`)
      return false
    } finally {
      setAdding(false)
    }
  }

  const handleAddSubmit = async () => {
    if (!name.trim()) {
      setFormError('Vul een naam in.')
      return
    }
    setFormError(null)
    const ok = await addItem({
      name: name.trim(),
      type,
      dosage: dosage.trim(),
      schedule: schedule.trim(),
    })
    if (ok) {
      setName('')
      setType('supplement')
      setDosage('')
      setSchedule('')
      setShowAddForm(false)
    }
  }

  const handleSuggestion = async () => {
    // Explicit one-tap suggestion — only ever added by user action.
    await addItem({
      name: 'Magnesium',
      type: 'supplement',
      dosage: '400 mg',
      schedule: 'Dagelijks bij het avondeten',
    })
  }

  const handleToggleTaken = async (item: HealthItem, taken: boolean) => {
    if (pendingItemId !== null) return
    setPendingItemId(item.id)
    setActionError(null)
    try {
      const log = await repositories.health.setTaken(
        item.id,
        today,
        taken,
        taken ? nowLocalTime() : undefined,
      )
      setTakenToday((current) => ({ ...current, [item.id]: log.taken }))
      setSaveState('cloud_saved')
      onAnnounce(
        taken ? `${item.name} gemarkeerd als ingenomen.` : `${item.name} gemarkeerd als niet ingenomen.`,
      )
    } catch (error) {
      setSaveState('sync_failed')
      const message = errorMessage(error)
      setActionError(message)
      showToast({ message, tone: 'error' })
      onAnnounce(`Bijwerken mislukt. ${message}`)
    } finally {
      setPendingItemId(null)
    }
  }

  const handleRemove = async () => {
    if (!removeTarget) return
    setRemoving(true)
    setActionError(null)
    try {
      await repositories.health.removeItem(removeTarget.id)
      setItems((current) => current.filter((item) => item.id !== removeTarget.id))
      setSaveState('cloud_saved')
      showToast({ message: `${removeTarget.name} verwijderd`, tone: 'success' })
      onAnnounce(`${removeTarget.name} is verwijderd uit je bibliotheek.`)
      setRemoveTarget(null)
    } catch (error) {
      setSaveState('sync_failed')
      const message = errorMessage(error)
      setActionError(message)
      showToast({ message, tone: 'error' })
    } finally {
      setRemoving(false)
    }
  }

  return (
    <section className="health-section" aria-labelledby="health-library-title">
      <h2 className="health-section-title" id="health-library-title">
        <PillIcon size={20} className="health-section-icon" />
        Supplementen en medicatie
      </h2>

      {loading ? (
        <Skeleton lines={3} />
      ) : loadError ? (
        <Banner tone="error">
          {loadError.userMessage}{' '}
          <Button variant="ghost" size="sm" onClick={() => void load()}>
            Opnieuw proberen
          </Button>
        </Banner>
      ) : (
        <>
          {items.length === 0 ? (
            <>
              <EmptyState
                icon={<PillIcon size={28} />}
                title="Je bibliotheek is leeg"
                body="Voeg zelf supplementen of medicatie toe om bij te houden wat je neemt."
              />
              <Card padding="md" className="health-section">
                <p className="health-library-item-name">Suggestie om mee te beginnen</p>
                <p className="health-library-item-meta">
                  Magnesium · 400 mg · dagelijks bij het avondeten
                </p>
                <p className="health-hint">
                  Wordt alleen toegevoegd als je hier zelf op tikt.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={adding}
                  disabled={adding}
                  onClick={() => void handleSuggestion()}
                >
                  Voeg toe
                </Button>
              </Card>
            </>
          ) : (
            <ul className="health-library-list">
              {items.map((item) => (
                <li key={item.id}>
                  <Card padding="sm">
                    <div className="health-library-item">
                      <div className="health-library-item-main">
                        <p className="health-library-item-name">{item.name}</p>
                        <p className="health-library-item-meta">
                          {[TYPE_LABELS[item.type], item.dosage, item.schedule]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                        <Checkbox
                          label="Vandaag ingenomen"
                          checked={takenToday[item.id] ?? false}
                          onChange={(taken) => void handleToggleTaken(item, taken)}
                        />
                      </div>
                      <IconButton
                        aria-label={`${item.name} verwijderen`}
                        variant="danger"
                        disabled={pendingItemId === item.id || removing}
                        onClick={() => setRemoveTarget(item)}
                      >
                        <TrashIcon size={20} />
                      </IconButton>
                    </div>
                  </Card>
                </li>
              ))}
            </ul>
          )}

          {actionError ? <Banner tone="error">{actionError}</Banner> : null}

          {showAddForm ? (
            <Card padding="md" className="health-add-form">
              <TextField
                label="Naam"
                value={name}
                onChange={setName}
                required
                error={formError ?? undefined}
                placeholder="Bijv. Vitamine D"
                disabled={adding}
              />
              <Select label="Type" value={type} onChange={(v) => setType(v as HealthItemType)} options={TYPE_OPTIONS} />
              <TextField
                label="Dosering"
                value={dosage}
                onChange={setDosage}
                placeholder="Bijv. 400 mg of 1 capsule"
                disabled={adding}
              />
              <TextField
                label="Schema (vrije tekst)"
                value={schedule}
                onChange={setSchedule}
                placeholder="Bijv. dagelijks bij het ontbijt"
                disabled={adding}
              />
              <div className="schedule-actions">
                <Button
                  variant="primary"
                  fullWidth
                  loading={adding}
                  disabled={adding}
                  onClick={() => void handleAddSubmit()}
                >
                  Voeg toe aan bibliotheek
                </Button>
                <Button variant="ghost" fullWidth disabled={adding} onClick={() => setShowAddForm(false)}>
                  Annuleren
                </Button>
              </div>
            </Card>
          ) : (
            <Button variant="secondary" onClick={() => setShowAddForm(true)} disabled={adding}>
              <PlusIcon size={18} /> Item toevoegen
            </Button>
          )}

          <SyncStatus state={saveState} />
        </>
      )}

      <ConfirmDestructiveAction
        open={removeTarget !== null}
        title={removeTarget ? `${removeTarget.name} verwijderen?` : 'Verwijderen?'}
        body="Dit verwijdert het item uit je bibliotheek. Eerdere registraties blijven bewaard."
        confirmLabel="Verwijderen"
        onConfirm={() => void handleRemove()}
        onCancel={() => setRemoveTarget(null)}
      />
    </section>
  )
}
